// Import all language files from locales
import { en } from './locales/en';
import { ko } from './locales/ko';
import { zh } from './locales/zh';
import { ja } from './locales/ja';
import { es } from './locales/es';
import { vi } from './locales/vi';
import { id } from './locales/id';
import { th } from './locales/th';
import { fr } from './locales/fr';
import { de } from './locales/de';
import { pt } from './locales/pt';
import { hi } from './locales/hi';
import { bn } from './locales/bn';
import { tl } from './locales/tl';

// Import types
import type { UILanguage } from './types';

// Re-export types
export type { UILanguage, LocalizedContent } from './types';
export { SUPPORTED_LANGUAGES, CEFR_LEVELS, LEARNING_GOALS, getLocalizedText } from './types';
export type { LanguageInfo, CEFRLevel, CEFRLevelInfo, LearningGoal, LearningGoalInfo } from './types';

// Language type alias for backward compatibility
export type Language = UILanguage;

// All translations
export const translations: Record<UILanguage, Record<string, string>> = {
  en,
  ko,
  zh,
  ja,
  es,
  vi,
  id,
  th,
  fr,
  de,
  pt,
  hi,
  bn,
  tl,
};

// Language names for display
export const languageNames: Record<UILanguage, string> = {
  en: 'English',
  ko: '한국어',
  zh: '中文',
  ja: '日本語',
  es: 'Español',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  th: 'ภาษาไทย',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  hi: 'हिन्दी',
  bn: 'বাংলা',
  tl: 'Filipino',
};

// Language native names with flags for UI
export const languageDisplayInfo: Record<UILanguage, { name: string; nativeName: string; flag: string }> = {
  en: { name: 'English', nativeName: 'English', flag: '🇺🇸' },
  ko: { name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  zh: { name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  ja: { name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  es: { name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  vi: { name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  id: { name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  th: { name: 'Thai', nativeName: 'ภาษาไทย', flag: '🇹🇭' },
  fr: { name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  de: { name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  pt: { name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  hi: { name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  bn: { name: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩' },
  tl: { name: 'Filipino', nativeName: 'Filipino', flag: '🇵🇭' },
};

// Default language is English (for global audience)
export const defaultLanguage: UILanguage = 'en';

// Get translations for a specific language
export function getTranslations(lang: UILanguage): Record<string, string> {
  return translations[lang] || translations.en;
}

// Get a specific translation key
export function t(key: string, lang: UILanguage, params?: Record<string, string | number>): string {
  const langTranslations = translations[lang] || translations.en;
  let text = langTranslations[key] || translations.en[key] || key;

  // Replace parameters like {param} with actual values
  if (params) {
    Object.entries(params).forEach(([paramKey, value]) => {
      text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
    });
  }

  return text;
}

// Check if a language is supported
export function isLanguageSupported(lang: string): lang is UILanguage {
  return lang in translations;
}

// Get all supported language codes
export function getSupportedLanguages(): UILanguage[] {
  return Object.keys(translations) as UILanguage[];
}

// Map common language codes to supported UI languages
const langMap: Record<string, UILanguage> = {
  'zh': 'zh',
  'zh-cn': 'zh',
  'zh-tw': 'zh',
  'zh-hk': 'zh',
  'cn': 'zh',
  'tw': 'zh',
  'ja': 'ja',
  'ja-jp': 'ja',
  'jp': 'ja',
  'ko': 'ko',
  'ko-kr': 'ko',
  'kr': 'ko',
  'es': 'es',
  'es-es': 'es',
  'es-mx': 'es',
  'vi': 'vi',
  'vi-vn': 'vi',
  'vn': 'vi',
  'id': 'id',
  'id-id': 'id',
  'th': 'th',
  'th-th': 'th',
  'fr': 'fr',
  'fr-fr': 'fr',
  'fr-ca': 'fr',
  'de': 'de',
  'de-de': 'de',
  'pt': 'pt',
  'pt-br': 'pt',
  'pt-pt': 'pt',
  'br': 'pt',
  'hi': 'hi',
  'hi-in': 'hi',
  'bn': 'bn',
  'bn-bd': 'bn',
  'bn-in': 'bn',
  'bd': 'bn',
  'tl': 'tl',
  'fil': 'tl',
  'fil-ph': 'tl',
  'ph': 'tl',
  'en': 'en',
  'en-us': 'en',
  'en-gb': 'en',
  'en-au': 'en',
};

// Detect browser language and return best match
export function detectBrowserLanguage(): UILanguage {
  if (typeof navigator === 'undefined') return defaultLanguage;

  // Use navigator.languages array for better detection (ordered by user preference)
  const languages = navigator.languages || [navigator.language];

  for (const lang of languages) {
    if (!lang) continue;

    const normalizedLang = lang.toLowerCase();

    // Check full locale first (e.g., 'ko-KR')
    if (langMap[normalizedLang]) {
      return langMap[normalizedLang];
    }

    // Check base language code (e.g., 'ko')
    const baseLang = normalizedLang.split('-')[0];
    if (langMap[baseLang]) {
      return langMap[baseLang];
    }

    // Direct match for supported languages
    if (isLanguageSupported(baseLang)) {
      return baseLang;
    }
  }

  return defaultLanguage;
}

// Re-export individual language objects
export { en, ko, zh, ja, es, vi, id, th, fr, de, pt, hi, bn, tl };
